import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { methodologyService } from "../../lib/firebase";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Copy, Check, Search, Filter, ExternalLink, Shield, Terminal, Table, Grid3X3, ArrowLeft } from "lucide-react";
import { toast } from "../../components/ui/use-toast";
import NavBar from '../../components/NavBar';

interface GTFOBinsFunction {
  name: string;
  description: string;
  code: string;
  sudo: boolean;
  suid: boolean;
  capabilities: boolean;
  limited_suid: boolean;
  type: string;
}

interface GTFOBinsBinary {
  name: string;
  description: string;
  functions: GTFOBinsFunction[];
  url: string;
}

export const GTFOBinsPage = (): JSX.Element => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [gtfobinsData, setGtfobinsData] = useState<GTFOBinsBinary[]>([]);
  const [filteredData, setFilteredData] = useState<GTFOBinsBinary[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFunction, setSelectedFunction] = useState<string>("all");
  const [copiedCommand, setCopiedCommand] = useState<string | null>(null);
  const [gtfobinsEnabled, setGtfobinsEnabled] = useState<boolean | null>(null);
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        await checkGTFOBinsAccess();
        await loadGTFOBinsData();
      } else {
        navigate('/signin');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [auth, navigate]);

  const checkGTFOBinsAccess = async () => {
    try {
      const settings = await methodologyService.getSystemSettings();
      setGtfobinsEnabled(!settings.disableGTFOBins);
      
      if (settings.disableGTFOBins) {
        setError("GTFOBins is currently disabled by the administrator.");
      }
    } catch (error) {
      console.error("Error checking GTFOBins access:", error);
      setError("Failed to check GTFOBins access.");
    }
  };

  const loadGTFOBinsData = async () => {
    try {
      setLoading(true);
      // Fetch the complete GTFOBins dataset from the local JSON file
      const response = await fetch('/gtfobins.json');
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setGtfobinsData(data);
      setFilteredData(data);
    } catch (error) {
      console.error("Error loading GTFOBins data:", error);
      setError("Failed to load GTFOBins data. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCommand = async (command: string) => {
    try {
      await navigator.clipboard.writeText(command);
      setCopiedCommand(command);
      toast({
        title: "Command Copied",
        description: "Command has been copied to clipboard.",
      });
      setTimeout(() => setCopiedCommand(null), 2000);
    } catch (error) {
      console.error("Failed to copy command:", error);
      toast({
        title: "Copy Failed",
        description: "Failed to copy command to clipboard.",
        variant: "destructive",
      });
    }
  };

  const handleSignOut = async () => {
    try {
      await auth.signOut();
      navigate('/signin');
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  // Filter data based on search and function type
  useEffect(() => {
    let filtered = gtfobinsData;

    if (searchTerm) {
      filtered = filtered.filter(binary => 
        binary.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        binary.functions.some(func => 
          func.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
          func.description.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    if (selectedFunction !== "all") {
      filtered = filtered.filter(binary =>
        binary.functions.some(func => func.type === selectedFunction)
      );
    }

    setFilteredData(filtered);
  }, [gtfobinsData, searchTerm, selectedFunction]);

  // Get unique function types
  const functionTypes = Array.from(
    new Set(gtfobinsData.flatMap(binary => binary.functions.map(func => func.type)))
  ).sort();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#e1dcdc75] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#daed6e] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading GTFOBins data...</p>
        </div>
      </div>
    );
  }

  if (error || gtfobinsEnabled === false) {
    return (
      <div className="min-h-screen bg-[#e1dcdc75] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="mb-4">
            <Shield className="mx-auto h-12 w-12 text-gray-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Restricted</h2>
          <p className="text-gray-600 mb-4">{error || "GTFOBins is currently unavailable."}</p>
          <Button onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      <NavBar userName={auth.currentUser?.displayName || ''} onSignOut={handleSignOut} />
      
      {/* Main content */}
      <div className="container mx-auto px-4 pt-[160px] pb-12">
        <div className="bg-white rounded-[20px] p-8 shadow-lg">
          {/* Mobile back button */}
          <div className="lg:hidden mb-4">
            <button 
              onClick={() => navigate('/toolkits')}
              className="text-black opacity-60 hover:opacity-100 transition-opacity font-['Space_Grotesk']"
            >
              ← Back to Toolkits
            </button>
          </div>

          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center">
                  <Terminal className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="font-['Space_Grotesk'] font-bold text-2xl text-black mb-2">
                    GTFOBins - Unix Binaries for Privilege Escalation
                  </h1>
                  <p className="text-gray-600 mt-1">
                    Unix binaries and scripts for security testing - {gtfobinsData.length} entries available
                  </p>
                </div>
              </div>
            </div>

            {/* Search and Filters */}
            <div className="bg-white rounded-lg p-6 mb-6 border-2 border-[#E7E7E7]">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Search binaries, functions..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                <Select value={selectedFunction} onValueChange={setSelectedFunction}>
                  <SelectTrigger>
                    <SelectValue placeholder="Function Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Functions</SelectItem>
                    {functionTypes.map(funcType => (
                      <SelectItem key={funcType} value={funcType}>{funcType}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchTerm("");
                    setSelectedFunction("all");
                  }}
                  className="font-['Space_Grotesk'] text-black"
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Clear Filters
                </Button>
              </div>

              {/* View Mode Toggle */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">View Mode:</span>
                  <div className="flex border rounded-lg">
                    <Button
                      variant={viewMode === 'cards' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setViewMode('cards')}
                      className="rounded-r-none"
                    >
                      <Grid3X3 className="w-4 h-4 mr-2" />
                      Cards
                    </Button>
                    <Button
                      variant={viewMode === 'table' ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setViewMode('table')}
                      className="rounded-l-none"
                    >
                      <Table className="w-4 h-4 mr-2" />
                      Table
                    </Button>
                  </div>
                </div>
                <div className="text-sm text-gray-500">
                  {filteredData.length} {filteredData.length === 1 ? 'binary' : 'binaries'} found
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="space-y-4">
              {filteredData.length === 0 ? (
                <div className="text-center py-12">
                  <div className="mb-4">
                    <Terminal className="mx-auto h-12 w-12 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
                  <p className="text-gray-500">
                    Try adjusting your search terms or filters.
                  </p>
                </div>
              ) : viewMode === 'table' ? (
                // Table View
                <div className="overflow-x-auto border border-gray-300 rounded-lg">
                  <table className="w-full border-collapse border border-gray-300">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 font-semibold">Binary</th>
                        <th className="text-left py-3 px-4 font-semibold">Functions</th>
                        <th className="text-left py-3 px-4 font-semibold">Description</th>
                        <th className="text-left py-3 px-4 font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredData.map((binary) => (
                        <tr key={binary.name} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <button
                              onClick={() => navigate(`/toolkits/gtfobins/${binary.name}`)}
                              className="font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                            >
                              {binary.name}
                            </button>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">{binary.functions.length}</span>
                              <div className="flex flex-wrap gap-1">
                                {binary.functions.slice(0, 3).map((func, index) => (
                                  <Badge key={index} variant="secondary" className="text-xs">
                                    {func.type}
                                  </Badge>
                                ))}
                                {binary.functions.length > 3 && (
                                  <Badge variant="secondary" className="text-xs">
                                    +{binary.functions.length - 3}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <p className="text-sm text-gray-600 truncate max-w-xs">
                              {binary.description || `${binary.name} binary for privilege escalation`}
                            </p>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => window.open(binary.url, '_blank')}
                              >
                                <ExternalLink className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                // Card View
                filteredData.map((binary) => (
                  <Card key={binary.name} className="border-2 border-[#E7E7E7]">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <CardTitle className="text-xl text-black">
                              <button
                                onClick={() => navigate(`/toolkits/gtfobins/${binary.name}`)}
                                className="text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                              >
                                {binary.name}
                              </button>
                            </CardTitle>
                            <Badge variant="outline" className="text-xs">
                              {binary.functions.length} function{binary.functions.length !== 1 ? 's' : ''}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">
                            {binary.description || `${binary.name} binary for privilege escalation`}
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(binary.url, '_blank')}
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          View Details
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {binary.functions.map((func, index) => (
                          <div key={index} className="border rounded-lg p-4 bg-gray-50">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <h4 className="font-semibold text-black capitalize">
                                    {func.name.replace('-', ' ')}
                                  </h4>
                                  <Badge variant="secondary" className="text-xs">{func.type}</Badge>
                                  {func.sudo && <Badge variant="outline" className="text-xs">Sudo</Badge>}
                                  {func.suid && <Badge variant="outline" className="text-xs">SUID</Badge>}
                                  {func.capabilities && <Badge variant="destructive" className="text-xs">Capabilities</Badge>}
                                  {func.limited_suid && <Badge variant="outline" className="text-xs">Limited SUID</Badge>}
                                </div>
                                <p className="text-sm text-gray-600 mb-2">{func.description}</p>
                              </div>
                              <Button
                                onClick={() => handleCopyCommand(func.code)}
                                size="sm"
                                variant="outline"
                                className="ml-4 flex-shrink-0"
                              >
                                {copiedCommand === func.code ? (
                                  <Check size={16} className="mr-1 text-green-600" />
                                ) : (
                                  <Copy size={16} className="mr-1" />
                                )}
                                {copiedCommand === func.code ? 'Copied!' : 'Copy'}
                              </Button>
                            </div>
                            {func.code && (
                              <div className="bg-black text-green-400 p-3 rounded text-sm font-mono overflow-x-auto">
                                <code>{func.code}</code>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>

            {/* Results count */}
            <div className="mt-6 text-center text-sm text-gray-500">
              Showing {filteredData.length} of {gtfobinsData.length} entries
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 